"use client"

import React from "react"
import { View, StyleSheet } from "react-native"
import { Text, ActivityIndicator } from "react-native-paper"
import { FileTreeView } from "../FileExplorer/FileTreeView"
import { FilePreview } from "../FileExplorer/FilePreview"
import type { FileNode, HierarchyResponse } from "../../../types/files"
import type { TabComponentProps } from "../../../types/tabs"
import { useTheme, Button } from "react-native-paper"
import { useApi } from "../../../contexts/ApiContext"

export function GalleryTab({ projectData, selectedNode, assetName }: TabComponentProps) {
  const theme = useTheme()
  const { hierarchyUrl, makeAbsoluteUrl, baseUrl } = useApi()
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [hierarchy, setHierarchy] = React.useState<FileNode | null>(null)
  const [selectedFolder, setSelectedFolder] = React.useState<FileNode | null>(null)
  const [corsError, setCorsError] = React.useState(false)

  React.useEffect(() => {
    const fetchHierarchy = async () => {
      // Use the assetName prop if available, otherwise fall back to selectedNode
      const effectiveAssetName = assetName || (selectedNode ? selectedNode.name : null)

      if (!effectiveAssetName) {
        console.log("No asset name available, cannot fetch hierarchy")
        return
      }

      setLoading(true)
      setError(null)
      setCorsError(false)

      try {
        console.log("Fetching hierarchy for asset:", effectiveAssetName)

        // Use the hierarchyUrl function from ApiContext
        const apiUrl = hierarchyUrl(effectiveAssetName)
        console.log("Fetching from URL:", apiUrl)

        const response = await fetch(apiUrl, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        })

        if (!response.ok) {
          throw new Error(`Failed to fetch hierarchy: ${response.status} ${response.statusText}`)
        }

        const data: HierarchyResponse = await response.json()
        console.log("Hierarchy data received:", data.status)
        setHierarchy(data.hierarchy)

        // Set the root as the initially selected folder
        setSelectedFolder(data.hierarchy)
      } catch (err) {
        console.error("Fetch error:", err)

        // Check if it's likely a CORS error
        if (err instanceof TypeError && err.message.includes("Failed to fetch")) {
          setCorsError(true)
          setError(
            "CORS error: Unable to access the API from this domain. The server may not allow cross-origin requests.",
          )
        } else {
          setError(err instanceof Error ? err.message : "An error occurred")
        }
      } finally {
        setLoading(false)
      }
    }

    fetchHierarchy()
  }, [selectedNode, assetName, hierarchyUrl])

  // Process file URLs to ensure they are absolute using the ApiContext
  const processFileUrl = (file: FileNode): FileNode => {
    if (!file || !file.url) return file

    // Make a copy of the file to avoid mutating the original
    const processedFile = { ...file }

    // Use the makeAbsoluteUrl function from ApiContext
    if (file.url) {
      processedFile.url = makeAbsoluteUrl(file.url)
    }

    return processedFile
  }

  // Process all files in a folder recursively
  const processFolderUrls = (folder: FileNode): FileNode => {
    if (!folder) return folder

    const processedFolder = { ...folder }

    if (processedFolder.children) {
      processedFolder.children = processedFolder.children.map((child) => {
        if (child.type === "file") {
          return processFileUrl(child)
        } else {
          return processFolderUrls(child)
        }
      })
    }

    return processedFolder
  }

  const handleFolderSelect = (folder: FileNode) => {
    setSelectedFolder(processFolderUrls(folder))
  }

  // Use assetName or selectedNode for display and error handling
  const effectiveAssetName = assetName || (selectedNode ? selectedNode.name : null)

  if (!effectiveAssetName) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <Text style={{ color: theme.colors.onSurfaceVariant }}>Please select a node to view gallery</Text>
      </View>
    )
  }

  if (loading) {
    return (
      <View style={[styles.container, styles.centerContent, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={{ color: theme.colors.onSurfaceVariant, marginTop: 16 }}>Loading file hierarchy...</Text>
      </View>
    )
  }

  if (error) {
    return (
      <View style={[styles.container, styles.centerContent, { backgroundColor: theme.colors.background }]}>
        <Text style={{ color: theme.colors.error, textAlign: "center", marginBottom: 16 }}>{error}</Text>

        {corsError && (
          <View style={[styles.corsErrorContainer, { backgroundColor: theme.colors.errorContainer }]}>
            <Text style={{ color: theme.colors.onSurfaceVariant, marginBottom: 16 }}>
              This is likely a CORS issue. Try these solutions:
            </Text>
            <Text style={{ color: theme.colors.onSurfaceVariant, marginBottom: 8 }}>
              1. The API server needs to add CORS headers to allow requests from your domain
            </Text>
            <Text style={{ color: theme.colors.onSurfaceVariant, marginBottom: 8 }}>
              2. You can use a CORS proxy for development
            </Text>
            <Text style={{ color: theme.colors.onSurfaceVariant, marginBottom: 16 }}>
              3. You can open the API URL directly in a new tab to verify it works
            </Text>

            <Button
              mode="contained"
              onPress={() => {
                const url = hierarchyUrl(effectiveAssetName)
                window.open(url, "_blank")
              }}
            >
              Open API in New Tab
            </Button>
          </View>
        )}
      </View>
    )
  }

  if (!hierarchy) {
    return (
      <View style={[styles.container, styles.centerContent, { backgroundColor: theme.colors.background }]}>
        <Text style={{ color: theme.colors.onSurfaceVariant }}>No files available</Text>
      </View>
    )
  }

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View
        style={[
          styles.splitView,
          {
            borderWidth: 1,
            borderColor: theme.colors.outline,
            borderRadius: 8,
          },
        ]}
      >
        <View
          style={[
            styles.treeView,
            {
              backgroundColor: theme.colors.elevation.level1,
              borderRightWidth: 1,
              borderRightColor: theme.colors.outline,
            },
          ]}
        >
          {/* Tree header removed as requested */}
          <FileTreeView data={hierarchy} onFolderSelect={handleFolderSelect} selectedFolder={selectedFolder} />
        </View>
        <View style={[styles.previewPanel, { backgroundColor: theme.colors.elevation.level2 }]}>
          <FilePreview folder={selectedFolder} />
        </View>
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  centerContent: {
    justifyContent: "center",
    alignItems: "center",
  },
  splitView: {
    flex: 1,
    flexDirection: "row",
    gap: 16,
    height: "100%",
    overflow: "hidden",
  },
  treeView: {
    width: 280,
    padding: 8,
    overflow: "auto",
  },
  treeHeader: {
    fontSize: 16,
    fontWeight: "500",
    marginBottom: 8,
    padding: 4,
  },
  previewPanel: {
    flex: 1,
    overflow: "hidden",
  },
  corsErrorContainer: {
    maxWidth: 600,
    padding: 16,
    borderRadius: 8,
  },
})

